import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { tagToneColors } from 'components/constants/tagTones';
import {
  aiPortraitHasContent,
  aiPortraitPlainText,
  aiPortraitPolled,
  aiPortraitSeverityTone,
  aiPortraitTopRisk,
  AiPortraitTone,
  aiStudentTabHref,
  isAiPortraitSeverity,
} from 'core/hooks/AiPortrait/aiPortraitModel';
import { useAiStudentPortrait } from 'core/hooks/AiPortrait/useAiStudentPortrait';

import {
  AI_PORTRAIT_ICON,
  AI_PORTRAIT_LIST_ICONS,
  AI_PORTRAIT_STALE_ICON,
} from '../AiPortrait/aiPortraitIcons';
import * as S from './StudentMiniCard.styled';

interface IMiniCardAiPortraitProps {
  studentId: string;
  /** Есть маршрут карточки студента — ссылки «весь портрет» и «создать». */
  canOpenCard: boolean;
}

/**
 * ТЗ 0104, агент 3 — короткий ИИ-портрет в мини-карточке: две-три строки
 * сводки и главный риск, ссылка на полный портрет во вкладке «Обзор».
 *
 * Свой запрос, ЛЕНИВЫЙ: раздел монтируется вместе с карточкой, а карточка —
 * только у открытого поповера (`StudentHoverCard`, `destroyTooltipOnHide`).
 * Документ — тот же, что у полной панели, и запись кеша одна: открытая
 * следом карточка студента рисует портрет сразу. Поля в
 * `StudentMiniCardView` не добавляются — запрос мини-карточки не тяжелеет.
 *
 * Портрета нет — раздела нет, только крошечная ссылка «создать» в карточку
 * (генерация — платный вызов, его подтверждают там, а не во всплывашке).
 * Монтирует его родитель, только при `canSeePortrait`.
 */
export const MiniCardAiPortrait = memo(
  ({ studentId, canOpenCard }: IMiniCardAiPortraitProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { portrait, loading, error, refetch } = useAiStudentPortrait(
      studentId,
    );
    const openCard = () =>
      history.push(aiStudentTabHref(studentId, 'overview'));
    const Icon = AI_PORTRAIT_ICON;

    if (loading && !portrait) {
      return (
        <S.Section aria-busy="true">
          <Title>
            <Icon size={12} strokeWidth={2} aria-hidden="true" />
            {t('aiPortrait.mini.title')}
          </Title>
          <S.Muted>{t('aiPortrait.mini.loading')}</S.Muted>
        </S.Section>
      );
    }

    if (error && !portrait) {
      return (
        <S.Section>
          <Title>
            <Icon size={12} strokeWidth={2} aria-hidden="true" />
            {t('aiPortrait.mini.title')}
          </Title>
          <S.Muted role="alert">
            {t('aiPortrait.mini.loadFailed')}
            <S.RetryLink type="button" onClick={refetch}>
              {t('aiPortrait.retry')}
            </S.RetryLink>
          </S.Muted>
        </S.Section>
      );
    }

    if (!portrait) return null;

    const polling = aiPortraitPolled(portrait.status);

    if (!aiPortraitHasContent(portrait)) {
      if (polling) {
        return (
          <S.Section>
            <Title>
              <Icon size={12} strokeWidth={2} aria-hidden="true" />
              {t('aiPortrait.mini.title')}
            </Title>
            <S.Muted role="status">{t('aiPortrait.mini.generating')}</S.Muted>
          </S.Section>
        );
      }
      return canOpenCard ? (
        <S.MoreHint>
          <TinyLink type="button" onClick={openCard}>
            <Icon size={12} strokeWidth={2} aria-hidden="true" />
            {t('aiPortrait.mini.generate')}
          </TinyLink>
        </S.MoreHint>
      ) : null;
    }

    const summary = aiPortraitPlainText(portrait.summary);
    const risk = aiPortraitTopRisk(portrait.risks);
    const RiskIcon = AI_PORTRAIT_LIST_ICONS.risks;
    const StaleIcon = AI_PORTRAIT_STALE_ICON;

    return (
      <S.Section>
        <Title>
          <Icon size={12} strokeWidth={2} aria-hidden="true" />
          {t('aiPortrait.mini.title')}
          <DraftMark>{t('aiPortrait.draft')}</DraftMark>
        </Title>
        {summary ? <Summary>{summary}</Summary> : null}
        {risk ? (
          <RiskRow>
            <RiskIcon
              size={12}
              strokeWidth={2}
              role="img"
              aria-label={t('aiPortrait.mini.topRisk')}
            />
            {risk.severity ? (
              <RiskTag $tone={aiPortraitSeverityTone(risk.severity)}>
                {isAiPortraitSeverity(risk.severity)
                  ? t(`aiPortrait.severity.${risk.severity}`)
                  : risk.severity}
              </RiskTag>
            ) : null}
            <RiskText>{risk.text}</RiskText>
          </RiskRow>
        ) : null}
        {polling ? (
          <S.Muted role="status">{t('aiPortrait.mini.generating')}</S.Muted>
        ) : portrait.stale ? (
          <StaleNote>
            <StaleIcon size={11} strokeWidth={2} aria-hidden="true" />
            {t('aiPortrait.mini.stale')}
          </StaleNote>
        ) : null}
        {canOpenCard ? (
          <S.MoreHint>
            <TinyLink type="button" onClick={openCard}>
              {t('aiPortrait.mini.openFull')}
            </TinyLink>
          </S.MoreHint>
        ) : null}
      </S.Section>
    );
  },
);

MiniCardAiPortrait.displayName = 'MiniCardAiPortrait';

const Title = styled(S.SectionTitle)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/** «AI — wersja robocza»: оценку человека не выдаём за факт. */
const DraftMark = styled.span`
  font-size: 10px;
  padding: 0 6px;
  border-radius: 10px;
  white-space: nowrap;
  text-transform: none;
  letter-spacing: 0;
  color: ${tagToneColors('gold').fg};
  background: ${tagToneColors('gold').bg};
`;

/** Три строки сводки — дальше многоточие; целиком — в карточке студента. */
const Summary = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #334155;
`;

const RiskRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;

  & > svg {
    flex-shrink: 0;
    align-self: center;
    color: ${tagToneColors('danger').fg};
  }
`;

const RiskTag = styled.span<{ $tone: AiPortraitTone }>`
  flex-shrink: 0;
  font-size: 11px;
  padding: 0 6px;
  border-radius: 10px;
  white-space: nowrap;
  color: ${({ $tone }) => tagToneColors($tone).fg};
  background: ${({ $tone }) => tagToneColors($tone).bg};
`;

const RiskText = styled.span`
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StaleNote = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  color: #5b6775;
  font-size: 11px;
`;

const TinyLink = styled(S.OpenLink)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
`;
